import * as React from 'react';
import Keycloak from 'keycloak-js';
import { config } from './config';
import { api, configureAuth } from './api/client';

export interface Identity {
  subject: string;
  username: string;
  displayName: string;
  email: string | null;
  roles: string[];
  organizations: string[];
}

/**
 * Exchanges the bearer token for a session cookie.
 *
 * A workspace or an app is opened by navigating to its URL in a new tab, and a
 * navigation cannot carry an Authorization header. The proxy therefore
 * authenticates those requests with a cookie, and something has to create it.
 *
 * The previous interface did this on every login. The rewrite did not, so the
 * cookie never existed and every workspace answered "missing authenticated
 * session" - the platform launched the workspace correctly and then refused to
 * let anyone in.
 *
 * Failure is deliberately not fatal: the rest of the application uses bearer
 * tokens and works without it. Only proxied access needs the cookie, and that
 * is where the refusal will be visible.
 */

async function openBackendSession(): Promise<void> {
  try {
    await api.post('/api/v1/auth/session', undefined);
  } catch (cause) {
    console.error('could not open a backend session; workspace access will be refused', cause);
  }
}

/**
 * Un second essai avant de declarer la session finie.
 *
 * Le navigateur sait souvent qu'il est hors ligne : dans ce cas la question ne
 * se pose pas, la session n'a rien perdu et l'utilisateur n'a rien a refaire.
 * Sinon on retente une fois, apres un court delai - un jeton de
 * rafraichissement reellement rejete echouera de nouveau tout de suite, alors
 * qu'une coupure passagere aura souvent cesse.
 */
async function refreshSurvives(keycloak: Keycloak): Promise<boolean> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  if (checking) return true;
  // Re-entrancy guard: the retry below fails the same way as the first
  // attempt, and keycloak-js reports that failure through onAuthRefreshError -
  // which lands back here. Without this the two would take turns retrying.
  checking = true;
  try {
    await new Promise((resolve) => setTimeout(resolve, 1500));
    try {
      await keycloak.updateToken(30);
      return true;
    } catch {
      return false;
    }
  } finally {
    checking = false;
  }
}

/** True while a second chance is already in flight. */
let checking = false;

async function closeBackendSession(): Promise<void> {
  try {
    await api.delete('/api/v1/auth/session');
  } catch {
    // Signing out must not be blocked by a cookie that may already be gone.
  }
}

// `expired` is distinct from `anonymous`: somebody who never signed in is
// invited to; somebody whose session ended in the middle of their work is told
// that is what happened, because otherwise the platform looks broken.
export type AuthStatus = 'initialising' | 'authenticated' | 'anonymous' | 'expired' | 'failed';

interface AuthContextValue {
  status: AuthStatus;
  identity: Identity | null;
  error: unknown;
  isAdmin: boolean;
  login: () => void;
  logout: () => void;
  accountUrl: string | null;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

interface TokenClaims {
  sub?: string;
  preferred_username?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  email?: string;
  realm_access?: { roles?: string[] };
  resource_access?: Record<string, { roles?: string[] }>;
  organizations?: unknown;
  organization?: unknown;
  groups?: unknown;
}

function readOrganizations(claims: TokenClaims): string[] {
  // Keycloak organisations surface differently depending on how the realm is
  // configured (an array, an object keyed by alias, or group paths), so all
  // three shapes are accepted rather than assuming one.
  const candidates = [claims.organizations, claims.organization, claims.groups];
  const found = new Set<string>();
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      for (const entry of candidate) {
        if (typeof entry === 'string') found.add(entry.replace(/^\//, ''));
        else if (entry && typeof entry === 'object' && 'alias' in entry) {
          const alias = (entry as { alias?: unknown }).alias;
          if (typeof alias === 'string') found.add(alias);
        }
      }
    } else if (candidate && typeof candidate === 'object') {
      for (const key of Object.keys(candidate as Record<string, unknown>)) found.add(key);
    }
  }
  return [...found];
}

function toIdentity(claims: TokenClaims, clientId: string): Identity {
  const username = claims.preferred_username ?? claims.sub ?? 'inconnu';
  const displayName =
    claims.name ??
    [claims.given_name, claims.family_name].filter(Boolean).join(' ').trim() ??
    username;
  const roles = new Set<string>([
    ...(claims.realm_access?.roles ?? []),
    ...(claims.resource_access?.[clientId]?.roles ?? []),
  ]);
  return {
    subject: claims.sub ?? username,
    username,
    displayName: displayName || username,
    email: claims.email ?? null,
    roles: [...roles],
    organizations: readOrganizations(claims),
  };
}

const ADMIN_ROLES = ['admin', 'noryx-admin', 'platform-admin', 'realm-admin'];
const AUTH_SCOPE = 'openid profile email';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = React.useState<AuthStatus>('initialising');
  const [identity, setIdentity] = React.useState<Identity | null>(null);
  const [error, setError] = React.useState<unknown>(null);
  const keycloakRef = React.useRef<Keycloak | null>(null);

  React.useEffect(() => {
    // Header mode is the local development path: the API trusts X-Noryx-User
    // and no identity provider is involved.
    if (config.authMode === 'header') {
      const devUser = new URLSearchParams(window.location.search).get('user') ?? 'noryx';
      configureAuth({ devUser });
      setIdentity({
        subject: devUser,
        username: devUser,
        displayName: devUser,
        email: null,
        roles: ['admin'],
        organizations: [],
      });
      setStatus('authenticated');
      return;
    }

    if (!config.oidc) {
      setError(new Error("Aucun fournisseur d'identite n'est configure pour cette instance."));
      setStatus('failed');
      return;
    }

    // One place decides that the session is over, so the reason is recorded
    // once and every path - a failed refresh, an explicit refresh error from
    // keycloak-js - lands on the same screen.
    let ended = false;
    const endSession = () => {
      if (ended) return;
      ended = true;
      setIdentity(null);
      setStatus('expired');
    };

    const keycloak = new Keycloak({
      url: config.oidc.url,
      realm: config.oidc.realm,
      clientId: config.oidc.clientId,
    });
    keycloakRef.current = keycloak;
    // Do not let keycloak-js reuse a deep or stateful current URL as the
    // callback URL. Some browsers can grow it past Keycloak's URI limit.
    const redirectUri = `${window.location.origin}/`;
    configureAuth({
      getToken: async () => {
        // A request can happen while React is still mounting providers. Never
        // ask keycloak-js to refresh a token before the initial OIDC callback
        // has been processed: it can restart the authorization flow forever.
        if (!keycloak.authenticated || !keycloak.token) return null;
        try {
          // Refresh when the token has under 30s left, so a long-running page
          // never sends an expired bearer.
          await keycloak.updateToken(30);
        } catch {
          // A failed refresh is two different events, and they were treated as
          // one. The identity provider rejecting the refresh token means the
          // session really is over - it expired, or Keycloak restarted and
          // dropped the sessions it holds in memory. A refresh that never
          // reached Keycloak means nothing about the session at all.
          //
          // Signing somebody out on the second is the platform punishing them
          // for their network. On a site where the connection drops now and
          // then, an access token that lives five minutes turns every brief
          // interruption into a sign-in screen, and the person reports losing
          // the platform periodically - which is exactly what it looks like
          // from their side.
          //
          // So the request is given one more chance before the session is
          // declared over. A genuinely rejected refresh token fails again
          // immediately; an interruption usually will not.
          if (!(await refreshSurvives(keycloak))) {
            // Returning null alone sent the request with no Authorization
            // header and let the screen render the API's answer: "missing
            // bearer token". That is the platform explaining its own internals
            // to somebody who left the page open for five minutes. Say the
            // session ended, and show the sign-in screen.
            endSession();
            return null;
          }
        }
        return keycloak.token ?? null;
      },
      onUnauthorized: () => {
        // Providers such as theme preferences can make a harmless request
        // before OIDC has finished initialising. A global 401 must never
        // alter the authentication state: callers receive the ApiError and
        // decide how to render it, while the OIDC flow stays authoritative.
      },
    });

    keycloak
      .init({
        pkceMethod: 'S256',
        checkLoginIframe: false,
        redirectUri,
      })
      .then((authenticated) => {
        // keycloak-js reports its own refresh failures here, including the
        // background ones no request asked for. It fires on the first failure,
        // so pointing it straight at endSession undid the second chance given
        // above: the request-side retry was still running when the session had
        // already been declared over.
        keycloak.onAuthRefreshError = () => {
          void refreshSurvives(keycloak).then((survived) => {
            if (!survived) endSession();
          });
        };
        // A logout is not ambiguous. Somebody signed out, here or elsewhere in
        // the single sign-on session, and there is nothing to retry.
        keycloak.onAuthLogout = endSession;
        if (authenticated && keycloak.tokenParsed) {
          setIdentity(toIdentity(keycloak.tokenParsed as TokenClaims, config.oidc?.clientId ?? ''));
          setStatus('authenticated');
          void openBackendSession();
        } else {
          setStatus('anonymous');
        }
      })
      .catch((cause: unknown) => {
        setError(cause);
        setStatus('failed');
      });
  }, []);

  const value = React.useMemo<AuthContextValue>(
    () => ({
      status,
      identity,
      error,
      isAdmin: Boolean(identity?.roles.some((role) => ADMIN_ROLES.includes(role.toLowerCase()))),
      login: () => {
        const keycloak = keycloakRef.current;
        if (!keycloak) {
          setError(new Error("Le service d'authentification n'est pas encore initialise."));
          setStatus('failed');
          return;
        }
        try {
          // Navigate explicitly rather than delegating to the adapter. This
          // keeps the interaction reliable in Safari and guarantees that a
          // user click always leaves the anonymous screen.
          void keycloak
            .createLoginUrl({
              redirectUri: `${window.location.origin}/`,
              scope: AUTH_SCOPE,
            })
            .then((url) => window.location.assign(url))
            .catch((cause: unknown) => {
              setError(cause);
              setStatus('failed');
            });
        } catch (cause) {
          setError(cause);
          setStatus('failed');
        }
      },
      logout: () => {
        // Drop the proxy cookie first: signing out of the identity provider
        // must not leave a session behind that still opens workspaces.
        void closeBackendSession().finally(() => {
          void keycloakRef.current?.logout({ redirectUri: window.location.origin });
        });
      },
      accountUrl: keycloakRef.current?.createAccountUrl() ?? null,
    }),
    [status, identity, error],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
